import { i18n } from "@/main";
import { dbKey } from "./dbKey";

/**
 * Tłumaczenie wartości pochodzących z bazy danych („słownik db”).
 *
 * Problem: część tekstów widocznych w UI nie powstaje na froncie — opisy grup ról
 * i uprawnień (`access.group_roles`, `access.role`), tytuły zgłoszeń itd. siedzą
 * w bazie po polsku, więc przy zmianie języka zostawały nieprzetłumaczone.
 *
 * Rozwiązanie: klucz liczony z treści zdania (patrz `dbKey`) + słownik w
 * `src/locales/db/<lang>.json` wczytywany do i18n pod przestrzeń nazw `db`.
 * Jedno tłumaczenie obsługuje każde miejsce, w którym dana wartość się pojawi
 * — niezależnie od tego, który endpoint ją zwrócił.
 *
 * Brak tłumaczenia nigdy nie psuje widoku: zwracany jest oryginał z bazy,
 * a w trybie deweloperskim w konsoli pojawia się gotowy klucz do dopisania.
 *
 * Użycie:
 *  - w szablonach (Options API): `{{ tdb(role.description) }}` — zarejestrowane globalnie w `main.js`,
 *  - w kodzie: `import { tdb } from "@/helpers/dbTranslate"`.
 */

// Klucze, o których już poinformowaliśmy w konsoli — żeby nie zaśmiecać jej w pętli renderowania.
const reportedMissingKeys = new Set();

function reportMissing(key, text) {
  if (process.env.NODE_ENV === "production" || reportedMissingKeys.has(key)) {
    return;
  }

  reportedMissingKeys.add(key);
  // eslint-disable-next-line no-console
  console.debug(
    `[i18n-db] Brak tłumaczenia wartości z bazy: "${text}"\n` +
    `          klucz: "${key}" — dopisz go w src/locales/db/<lang>.json`
  );
}

/**
 * Zwraca tłumaczenie wartości z bazy w aktualnym języku.
 *
 * @param {string|null|undefined} text Wartość z bazy (zwykle po polsku).
 * @returns {string|null|undefined} Tłumaczenie albo oryginał, gdy brak wpisu w słowniku.
 */
export function tdb(text) {
  if (text == null || text === "") {
    return text;
  }

  const original = String(text);
  const key = dbKey(original);
  if (!key) {
    return original;
  }

  const fullKey = `db.${key}`;
  const locale = i18n.global.locale?.value ?? i18n.global.locale;

  // `te` sprawdza konkretny język — bez tego zadziałałby fallback na `pl` i przy
  // brakującym tłumaczeniu widok pokazywałby polski tekst mimo wybranego języka
  // (efekt taki sam jak dziś, ale bez sygnału, że czegoś brakuje).
  if (i18n.global.te(fullKey, locale)) {
    const translated = i18n.global.t(fullKey);
    if (translated && translated !== fullKey) {
      return translated;
    }
  }

  reportMissing(fullKey, original);

  return original;
}

export default tdb;
